/**
 * HRMS job titles (canonical) → Portal `users.role` + defaults.
 * Single source for Staff Directory dropdowns and identity sync.
 */

export const HR_JOB_TITLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'teacher', label: 'Teacher' },
  { value: 'hod', label: 'Head of Department' },
  { value: 'coach', label: 'Sports / Activity Coach' },
  { value: 'vice_principal', label: 'Vice Principal' },
  { value: 'principal', label: 'Principal' },
  { value: 'academic_coordinator', label: 'Academic Coordinator' },
  { value: 'accountant', label: 'Accountant / Finance' },
  { value: 'admin_officer', label: 'Administrative Officer' },
  { value: 'hr_officer', label: 'HR Officer' },
  { value: 'librarian', label: 'Librarian' },
  { value: 'other_teaching', label: 'Other (Teaching)' },
  { value: 'other_non_teaching', label: 'Other (Non-Teaching)' },
];

const JOB_KEY_TO_PORTAL_ROLE: Record<string, string> = {
  teacher: 'teacher',
  hod: 'teacher',
  coach: 'teacher',
  other_teaching: 'teacher',
  vice_principal: 'vice_principal',
  principal: 'principal',
  academic_coordinator: 'vice_principal',
  accountant: 'finance_manager',
  admin_officer: 'staff',
  hr_officer: 'staff',
  librarian: 'staff',
  other_non_teaching: 'staff',
};

const KNOWN_KEYS = new Set(Object.keys(JOB_KEY_TO_PORTAL_ROLE));

/** Default module access for Portal launcher (before overrides in Admin Panel). */
export function defaultSystemAccessForPortalRole(portalRole: string): {
  hrms: boolean;
  crm: boolean;
  erp: boolean;
  lms: boolean;
  finance: boolean;
  website: boolean;
} {
  switch (portalRole) {
    case 'principal':
    case 'vice_principal':
      return { hrms: true, crm: true, erp: true, lms: true, finance: true, website: false };
    case 'teacher':
      return { hrms: true, crm: false, erp: true, lms: true, finance: false, website: false };
    case 'staff':
      return { hrms: true, crm: false, erp: true, lms: false, finance: false, website: false };
    case 'finance_manager':
    case 'finance_officer':
      return { hrms: true, crm: false, erp: true, lms: false, finance: true, website: false };
    default:
      return { hrms: true, crm: false, erp: true, lms: false, finance: false, website: false };
  }
}

/**
 * Map HR job title (dropdown value or legacy free text) + staff type → Portal `users.role`.
 */
export function resolvePortalRoleFromHrTitle(
  title: string | undefined,
  staffType?: 'teaching' | 'non-teaching'
): string {
  const raw = (title || '').trim();
  if (!raw) {
    return staffType === 'non-teaching' ? 'staff' : 'teacher';
  }
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  if (KNOWN_KEYS.has(key)) {
    return JOB_KEY_TO_PORTAL_ROLE[key] ?? 'staff';
  }
  const lower = raw.toLowerCase();
  if (lower.includes('vice principal') || lower.includes('vice-principal')) return 'vice_principal';
  if (lower.includes('principal') && !lower.includes('vice')) return 'principal';
  if (lower.includes('academic') && lower.includes('coordinat')) return 'vice_principal';
  if (lower.includes('teacher') || lower.includes('tutor')) return 'teacher';
  if (lower.includes('accountant') || lower.includes('finance')) return 'finance_manager';
  return staffType === 'non-teaching' ? 'staff' : 'teacher';
}

/** Legacy flat `permissions.*` flags on `users/{uid}` (alongside erpPermissions bundles). */
export function flatPermissionsForPortalRole(portalRole: string): Record<string, boolean> {
  const r = portalRole.toLowerCase();
  const isFinance = r === 'finance_manager' || r === 'finance_officer';
  const isLeadership = r === 'principal' || r === 'vice_principal' || r === 'super_admin';
  return {
    canViewDashboard: true,
    canManageAttendance: r === 'teacher' || isLeadership || r === 'admin',
    canManageCommunication: isLeadership || r === 'admin' || r === 'teacher',
    canManageExams: r === 'teacher' || isLeadership,
    canManageFinance: isFinance,
    canManageStudents: isLeadership || r === 'admin' || r === 'erp_manager',
    canManageTeachers: isLeadership || r === 'admin' || r === 'hr_manager',
    canViewReports: isFinance || isLeadership || r === 'admin' || r === 'staff',
  };
}
